import { QueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink, TRPCClientError } from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import superjson from 'superjson';
import { ERROR_CODES, type ErrorCode } from '@seo/contracts';
import type { AppRouter } from '@seo/api/src/routers';

export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

export const API_BASE = import.meta.env.VITE_API_URL ?? '';

export function createClient() {
  return createTRPCClient<AppRouter>({
    links: [
      httpBatchLink({
        url: `${API_BASE}/api/trpc`,
        transformer: superjson,
        // Session lives in an httpOnly cookie; the browser must be told to send it.
        fetch: (url, options) => fetch(url, { ...options, credentials: 'include' } as RequestInit),
      }),
    ],
  });
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // Retrying a 403 or a 409 only delays the error the user needs to see.
          const code = appCodeOf(error);
          if (code && NON_RETRYABLE.has(code)) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

const NON_RETRYABLE = new Set<ErrorCode>([
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'GEO_SCOPE_DENIED',
  'VALIDATION_FAILED',
  'NOT_FOUND',
  'CONFLICT',
  'VERSION_CONFLICT',
  'BLOCK_LOCKED',
  'CONFIRMATION_REQUIRED',
]);

interface TrpcErrorData {
  appCode?: ErrorCode;
  publicMessage?: string;
  fieldErrors?: Record<string, string[] | undefined>;
  meta?: Record<string, unknown>;
}

const ERROR_CODE_SET = new Set<string>(ERROR_CODES);

function trpcErrorData(error: unknown): TrpcErrorData | undefined {
  if (error === null || typeof error !== 'object' || !('data' in error)) return undefined;
  const data = error.data;
  return data !== null && typeof data === 'object' ? data : undefined;
}

/** Reads the stable error code the API attaches; never parses the message text. */
export function appCodeOf(error: unknown): ErrorCode | undefined {
  const code = trpcErrorData(error)?.appCode;
  return typeof code === 'string' && ERROR_CODE_SET.has(code) ? code : undefined;
}

export function publicMessageOf(error: unknown, fallback = 'Сталася помилка.'): string {
  const message = trpcErrorData(error)?.publicMessage;
  if (typeof message === 'string' && message !== '') return message;
  return error instanceof TRPCClientError
    ? fallback
    : error instanceof Error
      ? error.message
      : fallback;
}

export function fieldErrorsOf(error: unknown): Record<string, string> {
  const data = trpcErrorData(error);
  if (data === undefined) return {};
  const out: Record<string, string> = {};
  for (const [key, messages] of Object.entries(data?.fieldErrors ?? {})) {
    const first = messages?.[0];
    if (first) out[key] = first;
  }
  return out;
}
